import _ from 'lodash'

/**
 * Resource that represents an Antenna.
 *
 * Provided options:
 *   defaultRequestTimeout Timeout for device manager requests in milliseconds
 *
 * @name Antenna
 * @class
 * @augments TmrResource
 * @implements {EpcReaderInterface}
 * @prop {boolean} $started If true, the antenna is started
 */
export class Antenna {
  static ngType = 'provider'

  constructor() {
    'ngInject'
    this.defaultRequestTimeout = 5000
  }

  $get(
    $window,
    RfidReader,
    Zone,
    TmrResource,
    TmrWebSocket,
    TmrApi,
    EpcReaderInterface,
    tmrAntennaReadOperations,
    tmrAntennaWriteOperations,
    tmrAntennaKillOperations
  ) {
    'ngInject'
    this.Antenna = this.AntennaFactory(
      $window,
      RfidReader,
      Zone,
      TmrResource,
      TmrWebSocket,
      TmrApi,
      EpcReaderInterface,
      tmrAntennaReadOperations,
      tmrAntennaWriteOperations,
      tmrAntennaKillOperations,
      this.defaultRequestTimeout
    )
    return this.Antenna
  }

  AntennaFactory(
    $window,
    RfidReader,
    Zone,
    TmrResource,
    TmrWebSocket,
    TmrApi,
    EpcReaderInterface,
    tmrAntennaReadOperations,
    tmrAntennaWriteOperations,
    tmrAntennaKillOperations,
    defaultRequestTimeout
  ) {
    let actions = {
      /** @method Antenna#free */
      free: {
        method: 'GET',
        url: TmrApi.endpoint('Antenna.free'),
        isArray: true,
      },
    }

    let referencedProperties = [
      // antenna.rfidReader property will be instantiated as a RfidReader resource
      { property: 'rfidReader', resource: RfidReader },
      // antenna.zone property will be instantiated as a Zone resource
      { property: 'zone', resource: Zone },
    ]

    let defaultValues = () =>
      Object.assign(
        {
          $websocket: new TmrWebSocket(),
        },
        EpcReaderInterface.$defaultValues()
      )

    let AntennaResource = TmrResource(
      'Antenna',
      actions,
      referencedProperties,
      defaultValues
    )

    Object.assign(AntennaResource.prototype, EpcReaderInterface, {
      $start,
      $onStarted,
      $stop,
      $onStopped,
      $read,
      $onRead,
      $write,
      $onWritten,
      $kill,
      $describe,
    })

    return AntennaResource

    /**
     * Sets internal start parameters, then starts the antenna.
     *
     * @method Antenna#$start
     * @param {object} startParams Configuration map
     * @param {'persistent'|'realtime'} startParams.$mode
     * @param {TmrResource} [startParams.$itemService=Item]
     * @param {boolean} startParams.$working true if the antenna is working (and thus temporarily unavailable)
     * @param {boolean} [startParams.$decode=true] true if the antenna should decode (load items) from read epcs
     * @param {boolean} [startParams.$loadProducts=true] false to make the antenna skip loading products after decode. Effective only if `$decode` is `true`
     * @param {string=} startParams.$readOperation One of the presets from {@link tmrAntennaReadOperations}. Will add parameters to the query that instruct the device manager to read different parts of the tag
     * @param {object=} startParams.$queryParams Additional query parameters to send to the device manager
     * @param {function=} onMessageCallback Callback that is executed after the antenna has received a message.
     * If not provided, the antenna will try to read the epc from the message
     */
    function $start(startParams, onMessageCallback) {
      if (this.$working || this.$started) return this.$promise

      // Default options.
      const defaultParams = (({
        $decode,
        $loadProducts,
        $mode,
        $itemService,
        $readOperation,
      }) => ({
        $working: true,
        $decode,
        $loadProducts,
        $mode,
        $itemService,
        $readOperation,
      }))(EpcReaderInterface.$defaultValues())

      Object.assign(this, defaultParams, startParams)

      let params = { sessionId: TmrWebSocket.uuid }

      // Preset
      if (startParams.$readOperation)
        Object.assign(
          params,
          tmrAntennaReadOperations[startParams.$readOperation]
        )

      // Additional query parameters
      if (startParams.$queryParams)
        Object.assign(params, startParams.$queryParams)

      this.$promise = TmrApi.post(
        'Antenna.start',
        { id: this.id },
        this.rfidReader,
        { params, timeout: defaultRequestTimeout }
      )

      // Default message callback. Just passes the message to $readEpc
      onMessageCallback =
        onMessageCallback ?? (message => this.$readEpc.call(this, message))
      // Makes callback available to controllers, so they can trigger custom messages
      this.onMessageCallback = onMessageCallback

      return this.$promise
        .then(() => this.$onStarted(onMessageCallback))
        .finally(() => (this.$working = false))
    }

    /**
     * Callback executed after the antenna has started.
     */
    function $onStarted(onMessageCallback) {
      this.$started = true

      // Start the WebSocket.
      let url =
        TmrApi.endpoint('Antenna.start.websocket') +
        '?antennaId=' +
        this.id +
        '&sessionId=' +
        TmrWebSocket.uuid
      this.$websocket.start(url, onMessageCallback)

      // Try closing the connection when the window is closed.
      this.$onBeforeUnload = () => {
        if (this.$started) this.$stop()
      }
      $window.addEventListener('beforeunload', this.$onBeforeUnload)

      return this
    }

    /**
     * Sets $working and $started flag, then stops the antenna.
     *
     * @method Antenna#$stop
     */
    function $stop() {
      $window.removeEventListener('beforeunload', this.$onBeforeUnload)
      this.onMessageCallback = () => {}
      this.$working = true
      this.$started = false
      this.$websocket.stop()
      this.$clearQueues()
      this.$promise = TmrApi.post(
        'Antenna.stop',
        { id: this.id },
        this.rfidReader,
        {
          params: { sessionId: TmrWebSocket.uuid },
          timeout: defaultRequestTimeout,
        }
      )
      return this.$promise
        .then(() => this.$onStopped())
        .finally(() => (this.$working = false))
    }

    /**
     * Reads data from a tag.
     *
     * Note that this stops the antenna before reading.
     *
     * @example <caption>Using a preset from {@link tmrAntennaReadOperations}</caption>
     * antenna.$read('epc', { targetEpc: '123' })
     * @example <caption>Using custom parameters</caption>
     * antenna.$read({
     *   memoryBank: 'EPC',
     *   address: 2,
     *   numBlocks: 6,
     *   targetEpc: '1234567890'
     * })
     * @example <caption>Mixing preset with custom parameters</caption>
     * antenna.$read('epc', { targetEpc: '1234567890' })
     *
     * @method Antenna#$read
     * @param {string} operation The required parameters depend on the reader type. If you pass a string, it's interpreted
     *   as an epc.
     * @param {object|string} params Configuration map. The required parameters depend on the operation.
     * @param {string} params.memoryBank One of the {@link tmrAntennaMemoryBanks memory bank types}
     * @param {number} params.address
     * @param {number} params.numBlocks
     * @param {string} params.targetEpc The tag to read, if memoryBank is 'EPC'.
     */
    function $read(operation, params = {}) {
      if (typeof operation === 'string') {
        if (tmrAntennaReadOperations[operation] === undefined) {
          throw new Error(`Invalid antenna read operation "${operation}"`)
        }
        _.defaults(params, tmrAntennaReadOperations[operation])
      } else params = operation

      if (typeof params.address !== 'number') {
        throw new Error(
          'Missing or invalid address param for antenna read operation'
        )
      }
      if (typeof params.numBlocks !== 'number') {
        throw new Error(
          'Missing or invalid numBlocks param for antenna read operation'
        )
      }
      if (params.memoryBank === 'EPC' && typeof params.targetEpc !== 'string') {
        throw new Error(
          'Missing or invalid targetEpc param for antenna read epc operation'
        )
      }
      this.$working = true
      params.sessionId = TmrWebSocket.uuid
      let post = () =>
        TmrApi.post('Antenna.read', { id: this.id }, this.rfidReader, {
          params,
          timeout: defaultRequestTimeout,
        })
      this.$promise = this.$started ? this.$stop().then(post) : post()
      return this.$promise
        .then(res => this.$onRead(res.data.value))
        .finally(() => (this.$working = false))
    }

    /**
     * Writes data on a tag.
     *
     * Note that this stops the antenna before writing.
     *
     * @method Antenna#$write
     * @param {object} params Configuration map. The required parameters depend on the reader type.
     * @param {string} params.memoryBank One of the {@link tmrAntennaMemoryBanks memory bank types}
     * @param {number} params.address
     * @param {number} params.numBlocks
     * @param {string} params.targetEpc The tag to be written
     * @param {string} params.data The new value of the tag
     * @param {boolean=} verify
     *
     * @todo Validate params
     */
    function $write(operation, params = {}, verify) {
      if (typeof operation === 'string') {
        if (tmrAntennaWriteOperations[operation] === undefined) {
          throw new Error(`Invalid antenna write operation "${operation}"`)
        }
        _.defaults(params, tmrAntennaWriteOperations[operation])
      } else params = operation

      if (typeof params.address !== 'number') {
        throw new Error(
          'Missing or invalid address for antenna write operation'
        )
      }
      if (typeof params.numBlocks !== 'number') {
        throw new Error(
          'Missing or invalid numBlocks for antenna write operation'
        )
      }
      if (params.memoryBank === 'EPC' && typeof params.targetEpc !== 'string') {
        throw new Error(
          'Missing or invalid targetEpc for antenna write epc operation'
        )
      }
      if (params.memoryBank === 'EPC' && typeof params.data !== 'string') {
        throw new Error(
          'Missing or invalid data for antenna write epc operation'
        )
      }
      this.$working = true
      params.sessionId = TmrWebSocket.uuid
      let post = () =>
        TmrApi.post('Antenna.write', { id: this.id }, this.rfidReader, {
          params,
          timeout: defaultRequestTimeout,
        })
      this.$promise = this.$started ? this.$stop().then(post) : post()
      return this.$promise
        .then(() => this.$onWritten(verify))
        .finally(() => (this.$working = false))
    }

    /**
     * Kill a tag.
     *
     * Note that this stops the antenna before killing.
     *
     * @method Antenna#$kill
     * @param {object} params Configuration map. The required parameters depend on the reader type.
     * @param {string} params.targetEpc The tag to be written
     *
     * @todo Validate params
     */
    function $kill(operation, params = {}) {
      if (typeof operation === 'string') {
        if (tmrAntennaKillOperations[operation] === undefined) {
          throw new Error(`Invalid antenna kill operation "${operation}"`)
        }
        _.defaults(params, tmrAntennaKillOperations[operation])
      } else params = operation
      this.$working = true
      params.sessionId = TmrWebSocket.uuid
      let post = () =>
        TmrApi.post('Antenna.kill', { id: this.id }, this.rfidReader, {
          params,
          timeout: defaultRequestTimeout,
        })
      this.$promise = this.$started ? this.$stop().then(post) : post()
      return this.$promise.then(() => (this.$working = false))
    }

    /**
     * Callback executed after the antenna has stopped.
     *
     * @method Antenna#$onStopped
     */
    function $onStopped() {
      return this
    }

    /**
     * Callback executed after the antenna has read.
     *
     * @method Antenna#$onRead
     *
     * @param {string} epc
     */
    function $onRead(epc) {
      return epc
    }

    /**
     * Callback executed after the antenna has written a tag.
     *
     * @method Antenna#$onWritten
     *
     * @todo Implement here automatic verification of written data with Antenna.read()
     */
    function $onWritten(verify) {
      if (verify) {
      }
      return this
    }

    /**
     * Outputs a translatable description of the device.
     *
     * @method WorkstationAntennas#$describe
     */
    function $describe() {
      return {
        count: 1,
        readers: this.rfidReader.description,
      }
    }
  }
}
